import { withTimeout } from './withTimeout';

export type DetailedVoucherKind = 'receipt' | 'payment' | 'journal';
export type DetailedVoucherStatus = 'draft' | 'posted' | 'cancelled';
export interface DetailedVoucherLine {
  account_id: string; cost_center_id: string | null; side: 'debit' | 'credit'; amount_minor: string; description: string;
}
export interface DetailedVoucherInput {
  voucher_type: DetailedVoucherKind; voucher_number: string; currency: string; status: 'draft' | 'posted';
  description: string; document_date: string; property_id: string | null; tenant_id: string | null;
  payment_method: string | null; cheque_number: string | null; bank_reference: string | null; payee_name: string | null;
  lines: DetailedVoucherLine[];
}
export interface DetailedVoucher {
  property_label?: string | null;
  voucher: { id: string; voucher_type: DetailedVoucherKind; voucher_number: string; currency: string;
    status: DetailedVoucherStatus; description: string; property_id: string | null; tenant_id: string | null };
  details: { voucher_id: string; revision: number; state: DetailedVoucherStatus; document_date: string;
    payment_method: string | null; cheque_number: string | null; bank_reference: string | null; payee_name: string | null;
    cancellation_reason: string | null };
  lines: (DetailedVoucherLine & { line_number: number; account_code?: string; account_name?: string; account_type?: string; account_active?: boolean })[];
}
export interface DetailedVoucherClient {
  rpc(name: string, args: Record<string, unknown>): PromiseLike<{ data: unknown; error: { message: string; code?: string } | null }>;
}
export class DetailedVoucherError extends Error {
  constructor(public code: string) { super(code); }
}
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const voucherAscii = (value: string) => value.replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
  .replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d))).replace(/٫/g, '.');
export function voucherMinor(value: string): string {
  const normalized = voucherAscii(value).trim();
  // Do not guess ambiguous thousands separators, signs, exponents or partial values.
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) throw new DetailedVoucherError('invalidAmount');
  const [whole, fraction = ''] = normalized.split('.');
  const minor = BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0'));
  if (minor <= 0n || minor > 9007199254740991n) throw new DetailedVoucherError('invalidAmount');
  return minor.toString();
}
export function voucherDecimal(minor: string): string {
  const digits = minor.padStart(3, '0'); return `${digits.slice(0,-2)}.${digits.slice(-2)}`;
}
export function voucherDate(value: string): string {
  const normalized = voucherAscii(value).trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) throw new DetailedVoucherError('invalidDate');
  const date = new Date(`${normalized}T00:00:00Z`);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0,10) !== normalized) throw new DetailedVoucherError('invalidDate');
  return normalized;
}
async function rpc(client: DetailedVoucherClient, name: string, args: Record<string, unknown>) {
  const { data, error } = await withTimeout(client.rpc(name,args),20000,'Voucher request timed out');
  if (error) throw new DetailedVoucherError(error.code || 'unconfirmed');
  return data;
}
export async function detailedVoucherRequestId(client: DetailedVoucherClient): Promise<string> {
  const data = await rpc(client,'voucher_detail_request_id',{});
  if (typeof data !== 'string' || !uuid.test(data)) throw new DetailedVoucherError('unconfirmed');
  return data;
}
export async function detailedVoucherTenants(client: DetailedVoucherClient, propertyId: string): Promise<{id:string;first_name:string|null;last_name:string|null}[]> {
  const data = await rpc(client,'voucher_detail_tenants',{p_property_id:propertyId});
  if (!Array.isArray(data) || data.some(t=>!uuid.test(t?.id))) throw new DetailedVoucherError('unconfirmed');
  return data;
}
function validRead(data: DetailedVoucher, id: string): DetailedVoucher {
  if (!data?.voucher || data.voucher.id !== id || data.details?.voucher_id !== id
    || !Number.isSafeInteger(data.details.revision) || data.details.revision<1 || data.details.state !== data.voucher.status
    || !['draft','posted','cancelled'].includes(data.voucher.status)
    || !Array.isArray(data.lines) || data.lines.length<2 || data.lines.length>100 || data.lines.some((l,i)=>!l || l.line_number!==i+1
      || !uuid.test(l.account_id) || (l.cost_center_id!==null && !uuid.test(l.cost_center_id))
      || !['debit','credit'].includes(l.side) || typeof l.amount_minor!=='string' || !/^[1-9][0-9]{0,15}$/.test(l.amount_minor)
      || BigInt(l.amount_minor)>9007199254740991n)) throw new DetailedVoucherError('unconfirmed');
  return data;
}
function validMutation(data: unknown, expectedRevision: number, expectedId?: string): {id:string;revision:number} {
  if (!data || typeof data!=='object') throw new DetailedVoucherError('unconfirmed');
  const result=data as {id:string;revision:number};
  if (typeof result.id!=='string'||!uuid.test(result.id)||!Number.isSafeInteger(result.revision)||result.revision!==expectedRevision
    || (expectedId!==undefined&&result.id!==expectedId)) throw new DetailedVoucherError('unconfirmed');
  return result;
}
export async function readDetailedVoucher(client: DetailedVoucherClient, id: string): Promise<DetailedVoucher> {
  if(!uuid.test(id))throw new DetailedVoucherError('unconfirmed');
  return validRead(await rpc(client,'voucher_detail_read',{p_id:id}) as DetailedVoucher,id);
}
export async function probeDetailedVoucher(client: DetailedVoucherClient,id:string):Promise<{kind:'legacy';voucher_id:string}|{kind:'detailed';data:DetailedVoucher}> {
  if(!uuid.test(id))throw new DetailedVoucherError('unconfirmed');
  const value=await rpc(client,'voucher_detail_probe',{p_id:id}) as {kind:string;voucher_id:string;data:DetailedVoucher};
  if(value?.kind==='legacy'&&value.voucher_id===id)return {kind:'legacy',voucher_id:id};
  if(value?.kind==='detailed')return {kind:'detailed',data:validRead(value.data,id)};
  throw new DetailedVoucherError('unconfirmed');
}
export function verifyDetailedVoucher(value: DetailedVoucher, expected: DetailedVoucherInput, revision: number) {
  if (value.details.revision !== revision || ['voucher_type','voucher_number','currency','status','description','property_id','tenant_id']
    .some(key => value.voucher[key as keyof typeof value.voucher] !== expected[key as keyof DetailedVoucherInput])
    || ['document_date','payment_method','cheque_number','bank_reference','payee_name']
      .some(key=>value.details[key as keyof typeof value.details] !== expected[key as keyof DetailedVoucherInput])
    || value.lines.length !== expected.lines.length || expected.lines.some((line,i)=>Object.keys(line)
      .some(key=>line[key as keyof DetailedVoucherLine] !== value.lines[i][key as keyof DetailedVoucherLine]))) throw new DetailedVoucherError('unconfirmed');
}
export async function createDetailedVoucher(client: DetailedVoucherClient, input: DetailedVoucherInput, requestId: string, current: () => boolean) {
  if (!current()) throw new DetailedVoucherError('42501');
  const result = validMutation(await rpc(client,'voucher_detail_create',{p_request_id:requestId,p_payload:input}),1);
  if (!current()) throw new DetailedVoucherError('42501');
  const persisted = await readDetailedVoucher(client,result.id);
  if (!current()) throw new DetailedVoucherError('42501');
  verifyDetailedVoucher(persisted,input,result.revision);
  return persisted;
}
export async function updateDetailedVoucher(client:DetailedVoucherClient,id:string,revision:number,input:DetailedVoucherInput,requestId:string,current:()=>boolean) {
  if(!current())throw new DetailedVoucherError('42501');
  const result=validMutation(await rpc(client,'voucher_detail_update',{p_id:id,p_revision:revision,p_payload:input,p_request_id:requestId}),revision+1,id);
  if(!current())throw new DetailedVoucherError('42501');
  const persisted=await readDetailedVoucher(client,id);
  if(!current())throw new DetailedVoucherError('42501');
  verifyDetailedVoucher(persisted,input,result.revision);return persisted;
}
export async function transitionDetailedVoucher(client: DetailedVoucherClient, id: string, revision: number, status: 'posted' | 'cancelled',
  reason: string | null, requestId: string, current: () => boolean) {
  if (!current()) throw new DetailedVoucherError('42501');
  const result = validMutation(await rpc(client,'voucher_detail_transition',{p_id:id,p_revision:revision,p_status:status,p_reason:reason,p_request_id:requestId}),revision+1,id);
  if (!current()) throw new DetailedVoucherError('42501');
  const persisted = await readDetailedVoucher(client,result.id);
  if (!current()) throw new DetailedVoucherError('42501');
  if (persisted.voucher.status!==status || persisted.details.revision!==result.revision) throw new DetailedVoucherError('unconfirmed');
  return persisted;
}

export interface LegacyVoucherDraftInput {
  amount_minor: string; currency: string; voucher_number: string; description: string;
}
export interface LegacyVoucherDraft {
  voucher: DetailedVoucher['voucher'] & { updated_at: string };
  amount_minor: string;
}
const timestamp = (value: unknown): value is string => typeof value==='string'
  && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/.test(value)
  && Number.isFinite(Date.parse(value));
export async function readLegacyVoucherDraft(client:DetailedVoucherClient,id:string):Promise<LegacyVoucherDraft> {
  if(!uuid.test(id))throw new DetailedVoucherError('unconfirmed');
  const data=await rpc(client,'voucher_legacy_draft_read',{p_id:id}) as LegacyVoucherDraft;
  if(!data?.voucher||data.voucher.id!==id||data.voucher.status!=='draft'
    || !['receipt','payment'].includes(data.voucher.voucher_type)||!timestamp(data.voucher.updated_at)
    || typeof data.amount_minor!=='string'|| !/^[1-9][0-9]{0,15}$/.test(data.amount_minor)
    || BigInt(data.amount_minor)>9007199254740991n)throw new DetailedVoucherError('unconfirmed');
  return data;
}
export async function updateLegacyVoucherDraft(client:DetailedVoucherClient,id:string,updatedAt:string,
  input:LegacyVoucherDraftInput,requestId:string,current:()=>boolean):Promise<LegacyVoucherDraft> {
  if(!current())throw new DetailedVoucherError('42501');
  if(!uuid.test(id)||!timestamp(updatedAt))throw new DetailedVoucherError('unconfirmed');
  const result=await rpc(client,'voucher_legacy_draft_update',{
    p_id:id,p_updated_at:updatedAt,p_payload:input,p_request_id:requestId,
  }) as {id:string;updated_at:string};
  if(!result||result.id!==id||!timestamp(result.updated_at))throw new DetailedVoucherError('unconfirmed');
  if(!current())throw new DetailedVoucherError('42501');
  const persisted=await readLegacyVoucherDraft(client,id);
  if(!current())throw new DetailedVoucherError('42501');
  if(persisted.voucher.updated_at!==result.updated_at || persisted.amount_minor!==input.amount_minor
    || ['currency','voucher_number','description'].some(k=>persisted.voucher[k as keyof typeof persisted.voucher]!==input[k as keyof LegacyVoucherDraftInput]))
    throw new DetailedVoucherError('unconfirmed');
  return persisted;
}
